// The voice picker in the studio's sheet. Same endpoints and params as the
// modal's VoiceSelector (popular = /api/model by usesLast24Hours, search,
// recent) plus badge filters and the user's own voices. One tap picks; the
// page decides whether that replaces the voice or adds a duet partner.
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import millify from "millify";
import { useQuery } from "react-query";
import { getIdToken } from "../../utils/firebaseAuth";
import { Plus, Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthProvider";
import { type MessageKey, useT } from "@jammable/ui-core/src/i18n";
import { VoiceImage } from "./StudioChrome";

/* eslint-disable @typescript-eslint/no-explicit-any */

const LIMIT = 20;

// Badge strings as stored on models (lowercase); /api/model `filter`
// matches badges or classification.
const FILTERS: { id: string; label: MessageKey; auth?: boolean }[] = [
  { id: "trending", label: "studio.voices.trending" },
  { id: "recent", label: "studio.voices.recent", auth: true },
  { id: "mine", label: "studio.voices.mine", auth: true },
  { id: "music", label: "studio.voices.music" },
  { id: "cartoon", label: "studio.voices.cartoon" },
  { id: "gaming", label: "studio.voices.gaming" },
  { id: "anime", label: "studio.voices.anime" },
  { id: "public figure", label: "studio.voices.publicFigure" },
];

const authToken = async () => getIdToken();

// Voices people most often put in a duet with these, from the last week of
// duets (the old modal's "Recommended" tab). The API matches on the last id.
const fetchDuetMatches = async (ids: string[], limit: number) => {
  const { data } = await axios.get("/api/model/getDuetMatches", {
    params: { modelIds: ids.join(","), limit },
  });
  return (data?.models ?? []) as any[];
};

export const useDuetMatches = (voices: any[], limit = 10) => {
  const ids = voices.map((v) => v._id);
  return useQuery(
    ["studio-duet-matches", ids.join(","), limit],
    () => fetchDuetMatches(ids, limit),
    { enabled: ids.length > 0, staleTime: 10 * 60 * 1000 },
  );
};

const Avatar = ({ voice, size }: { voice: any; size: number }) => (
  <span
    className="tw-relative tw-block tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-bg-jam-line"
    style={{ width: size, height: size }}
  >
    <VoiceImage
      model={voice}
      fill
      sizes={`${size}px`}
      className="tw-object-cover"
    />
  </span>
);

// "Often paired with X": avatars with a lime +, one tap adds the voice.
export const DuetSuggestions = ({
  voices,
  onAdd,
}: {
  voices: any[];
  onAdd: (voice: any) => void;
}) => {
  const t = useT();
  const { data: matches = [], isLoading } = useDuetMatches(voices, 10);
  const shown = matches
    .filter((m) => !voices.some((v) => v._id === m._id))
    .slice(0, 8);
  const last = voices[voices.length - 1];
  if (!last) return null;
  // The same box while it loads, so the page doesn't jump when it lands.
  if (isLoading)
    return (
      <div
        aria-hidden
        className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2.5 tw-overflow-hidden tw-rounded-[24px] tw-bg-white tw-px-3 tw-pb-2.5 tw-pt-3"
      >
        <div className="tw-mx-1 tw-h-[18px] tw-w-44 tw-animate-pulse tw-rounded-full tw-bg-jam-lav" />
        <div className="tw-flex tw-gap-1 tw-pb-1">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="tw-flex tw-w-[76px] tw-shrink-0 tw-flex-col tw-items-center tw-gap-1.5 tw-px-1 tw-py-1.5"
            >
              <span className="tw-h-14 tw-w-14 tw-animate-pulse tw-rounded-full tw-bg-jam-lav" />
              <span className="tw-h-[13px] tw-w-12 tw-animate-pulse tw-rounded-full tw-bg-jam-lav" />
              <span className="tw-h-[9px]" />
            </div>
          ))}
        </div>
      </div>
    );
  if (shown.length === 0) return null;
  return (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2.5 tw-rounded-[24px] tw-bg-white tw-px-3 tw-pb-2.5 tw-pt-3">
      <div className="tw-px-1 tw-text-[13px] tw-font-extrabold tw-text-jam-ink">
        {t("studio.voices.pairedWith", { name: last.artistname })}
      </div>
      <div className="tw-flex tw-gap-1 tw-overflow-x-auto tw-pb-1 [&::-webkit-scrollbar]:tw-hidden">
        {shown.map((m) => (
          <button
            key={m._id}
            type="button"
            onClick={() => onAdd(m)}
            aria-label={t("studio.voices.add", { name: m.artistname })}
            className="tw-group tw-flex tw-w-[76px] tw-shrink-0 tw-cursor-pointer tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-[18px] tw-border-0 tw-bg-transparent tw-px-1 tw-py-1.5 tw-text-jam-ink hover:tw-bg-jam-lav"
          >
            <span className="tw-relative">
              <Avatar voice={m} size={56} />
              <span className="tw-absolute tw--bottom-0.5 tw--right-0.5 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid tw-border-white tw-bg-jam-lime tw-text-jam-ink">
                <Plus size={13} strokeWidth={3.5} />
              </span>
            </span>
            <span className="tw-line-clamp-2 tw-w-full tw-text-center tw-text-[11px] tw-font-extrabold tw-leading-tight">
              {m.artistname}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Who sings, as seats: one card per voice (tap to swap it) and an empty
// seat the same size to add a duet partner.
export const VoiceSeats = ({
  voices,
  max,
  onChange,
  onRemove,
  onAdd,
}: {
  voices: any[];
  max: number;
  onChange: (index: number) => void;
  onRemove: (voice: any) => void;
  onAdd: () => void;
}) => {
  const t = useT();
  const group = voices.length > 1;
  return (
    <div className="tw-grid tw-grid-cols-2 tw-gap-2.5 sm:tw-grid-cols-3">
      {voices.map((v, i) => (
        <div key={v._id} className="tw-relative tw-min-w-0">
          <button
            type="button"
            onClick={() => onChange(i)}
            aria-label={t("studio.voices.change", { name: v.artistname })}
            className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-[24px] tw-border-0 tw-bg-white tw-px-3 tw-pb-3.5 tw-pt-4 tw-text-jam-ink tw-transition-colors hover:tw-bg-jam-line"
          >
            <Avatar voice={v} size={76} />
            <span className="tw-w-full tw-min-w-0">
              <span className="tw-block tw-truncate tw-text-center tw-text-[15px] tw-font-extrabold">
                {v.artistname}
              </span>
              <span className="tw-block tw-text-center tw-text-xs tw-font-bold tw-text-jam-purple">
                {t("studio.change")}
              </span>
            </span>
          </button>
          {group && (
            <button
              type="button"
              aria-label={t("studio.voices.removeName", { name: v.artistname })}
              onClick={() => onRemove(v)}
              className="tw-absolute tw-right-2 tw-top-2 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lav tw-text-jam-ink hover:tw-bg-jam-line"
            >
              <X size={14} strokeWidth={3} />
            </button>
          )}
        </div>
      ))}
      {voices.length < max && (
        <button
          type="button"
          onClick={onAdd}
          className="tw-flex tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-[24px] tw-border-0 tw-bg-white/50 tw-px-3 tw-pb-3.5 tw-pt-4 tw-text-jam-ink tw-transition-colors hover:tw-bg-white"
        >
          <span className="tw-flex tw-h-[76px] tw-w-[76px] tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-jam-purple">
            <Plus size={30} strokeWidth={2.75} />
          </span>
          <span>
            <span className="tw-block tw-text-center tw-text-[15px] tw-font-extrabold">
              {t("studio.addVoice")}
            </span>
            <span className="tw-block tw-text-center tw-text-xs tw-font-bold tw-text-jam-muted">
              {group
                ? t("studio.voices.upTo", { max })
                : t("studio.voices.makeDuet")}
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

const fetchVoices = async (filter: string, search: string): Promise<any[]> => {
  if (search) {
    const { data } = await axios.get(`/api/model`, {
      params: {
        search,
        limit: LIMIT,
        page: 1,
        sortOrder: "desc",
        sortBy: "usesLast24Hours",
      },
    });
    return data?.data ?? [];
  }
  if (filter === "recent") {
    const token = await authToken();
    if (!token) return [];
    const { data } = await axios.get(
      `/api/home/getRecentVoices?limit=${LIMIT}`,
      { headers: { Authorization: token } },
    );
    return data?.voices ?? [];
  }
  if (filter === "mine") {
    const token = await authToken();
    if (!token) return [];
    const { data } = await axios.get("/api/user/getMyVoices", {
      params: { type: "uploaded", page: 1, limit: 50 },
      headers: { Authorization: token },
    });
    // Only voices that finished training can sing.
    return (data?.models ?? []).filter((m: any) => !m.training && !m.failed);
  }
  const { data } = await axios.get(`/api/model`, {
    params: {
      limit: LIMIT,
      sortOrder: "desc",
      sortBy: "usesLast24Hours",
      ...(filter !== "trending" ? { filter } : {}),
    },
  });
  return data?.data ?? [];
};

const VoiceTile = ({
  voice,
  disabled,
  onClick,
}: {
  voice: any;
  disabled?: boolean;
  onClick: () => void;
}) => {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="tw-flex tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-[26px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-px-2.5 tw-pb-3 tw-pt-3.5 tw-text-jam-ink tw-transition-colors hover:tw-border-jam-purple disabled:tw-cursor-default disabled:tw-opacity-40"
    >
      <span className="tw-relative tw-h-[60px] tw-w-[60px] tw-overflow-hidden tw-rounded-full">
        <VoiceImage
          model={voice}
          fill
          sizes="60px"
          className="tw-object-cover"
        />
      </span>
      <span className="tw-line-clamp-1 tw-w-full tw-text-center tw-text-[13px] tw-font-extrabold">
        {voice?.artistname}
      </span>
      <span className="tw-text-[11px] tw-font-bold tw-text-jam-muted">
        {t("studio.voices.uses", { n: millify(voice?.uses || 0) })}
      </span>
    </button>
  );
};

const VoicePicker = ({
  exclude,
  onPick,
  pairWith = [],
}: {
  exclude: any[];
  onPick: (voice: any) => void;
  // Adding a duet partner: lead with voices often paired with these.
  pairWith?: any[];
}) => {
  const { user } = useAuth();
  const t = useT();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const pairing = pairWith.length > 0;
  const [filter, setFilter] = useState(pairing ? "paired" : "trending");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Desktop only: on phones focusing would pop the keyboard over the grid.
    if (window.matchMedia("(min-width: 768px)").matches) {
      searchRef.current?.focus({ preventScroll: true });
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const showPaired = pairing && filter === "paired" && !debounced;
  const paired = useDuetMatches(pairWith, 20);
  // No duets with this voice yet: go straight to Trending and drop the
  // empty "Often with" filter rather than show a dead end.
  const noPairs =
    pairing && paired.isFetched && (paired.data || []).length === 0;
  useEffect(() => {
    if (noPairs && filter === "paired") setFilter("trending");
  }, [noPairs, filter]);
  const listed = useQuery(
    ["studio-voices", debounced ? "search" : filter, debounced, !!user],
    () => fetchVoices(filter, debounced),
    {
      keepPreviousData: true,
      staleTime: 60 * 1000,
      enabled: !showPaired,
    },
  );
  const voices: any[] = (showPaired ? paired.data : listed.data) ?? [];
  const isFetching = showPaired ? paired.isFetching : listed.isFetching;

  const filters = [
    ...(pairing && !noPairs
      ? [
          {
            id: "paired",
            label: t("studio.voices.oftenWith", {
              name: pairWith[pairWith.length - 1]?.artistname,
            }),
          },
        ]
      : []),
    ...FILTERS.filter((f) => !f.auth || !!user).map((f) => ({
      ...f,
      label: t(f.label),
    })),
  ];
  const taken = (v: any) => exclude.some((s) => s._id === v._id);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3.5">
      <div className="tw-flex tw-items-center tw-gap-2">
        <label className="tw-flex tw-flex-grow tw-items-center tw-gap-2.5 tw-rounded-full tw-bg-jam-lav tw-px-5 tw-py-3.5">
          <Search
            size={18}
            strokeWidth={2.5}
            className="tw-shrink-0 tw-text-jam-muted"
          />
          <input
            ref={searchRef}
            aria-label={t("studio.voices.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("studio.voices.search")}
            className="tw-w-full tw-border-0 tw-bg-transparent tw-text-[15px] tw-font-bold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted"
          />
          {search && (
            <button
              type="button"
              aria-label={t("studio.voices.clear")}
              onClick={() => setSearch("")}
              className="tw-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-jam-muted"
            >
              <X size={18} />
            </button>
          )}
        </label>
      </div>

      {!debounced && (
        <div className="tw-flex tw-gap-2 tw-overflow-x-auto tw-pb-1 md:tw-flex-wrap md:tw-overflow-visible">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`tw-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-3.5 tw-py-[7px] tw-text-[13px] tw-font-extrabold ${
                filter === f.id
                  ? "tw-bg-jam-ink tw-text-white"
                  : "tw-bg-jam-lav tw-text-jam-ink hover:tw-text-jam-purple"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {isFetching && voices.length === 0 ? (
        <div className="tw-grid tw-grid-cols-3 tw-gap-2.5 sm:tw-grid-cols-4 md:tw-grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className="tw-h-[130px] tw-animate-pulse tw-rounded-[26px] tw-bg-jam-lav"
            />
          ))}
        </div>
      ) : voices.length === 0 ? (
        <div className="tw-rounded-[22px] tw-bg-jam-lav tw-p-7 tw-text-center tw-text-sm tw-font-bold tw-text-jam-muted">
          {debounced
            ? t("studio.voices.noMatch")
            : filter === "mine"
              ? t("studio.voices.noMine")
              : showPaired
                ? t("studio.voices.noPairs")
                : t("studio.empty")}
        </div>
      ) : (
        <div
          className={`tw-grid tw-grid-cols-3 tw-gap-2.5 tw-overflow-y-auto sm:tw-grid-cols-4 md:tw-grid-cols-5 ${isFetching ? "tw-opacity-60" : ""}`}
        >
          {voices.map((v: any) => (
            <VoiceTile
              key={v._id}
              voice={v}
              disabled={taken(v)}
              onClick={() => onPick(v)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VoicePicker;
